import React, { useMemo, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { isEmpty, reduce, omit, concat, forIn } from 'lodash'
import { Redirect, useHistory } from 'react-router-dom'
import { RecordTypes } from 'flatfile-config'
import {
  ServicesAgreementPlanType,
  ServicesAgreementPlanStatus,
  NO_SERVICES_AGREEMENT_OPTION,
  ReasonForDiscard,
  ActiveBenefitsPlanType,
  GrandfatheredUserValue,
} from 'config/automatic-rollovers'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'
import { PlansExceedingUndertakings } from 'config/products'
import { IneligiblePlanModal, AllParticipantsRemovedModal } from '../components'
import {
  downloadDiscardedParticipants,
  getSelectInitialValue,
  getRemoveFromSubmissionOption,
} from 'utils'

const planTypes = Object.values(ServicesAgreementPlanType)
const planStatuses = Object.values(ServicesAgreementPlanStatus)
const removeFromSubmissionOption = getRemoveFromSubmissionOption(
  ActiveBenefitsPlanType.NONE
)

const ActiveBenefitExceedingThresholdParagraphOne =
  'This submission contains participants with accrued benefits in excess of $7,000 in an ACTIVE tax- qualified retirement plan. Generally, the Internal Revenue Code prohibits involuntary distribution of participants\u2019 accrued benefits with a value exceeding $7,000.'
const ActiveBenefitExceedingThresholdParagraphTwo =
  'In limited circumstances, participant benefits in excess of $7,000 may be eligible for involuntary distribution and rollover to an IRA. <strong>Plan Fiduciary determines whether a participant\u2019s benefit is an amount eligible for involuntary distribution and rollover to an IRA under the Code.</strong>'

const ActiveBenefitTerminatingDefinedParagraphOne =
  'This submission contains participants in a terminating defined benefit plan. Participants of terminating defined benefit plans are eligible for involuntary distribution to an IRA if the plan is not covered by the PBGC.'
const ActiveBenefitTerminatingDefinedParagraphTwo =
  'Most private sector defined benefit plans are covered by the PBGC, but there are some exceptions. <strong>Plan Fiduciary determines whether or not the plan is covered by the PBGC and if participant balances are eligible for involuntary distribution to IRA.</strong>'

const ActiveBenefitTerminatingDefinedParagraphOne_TAXABLE =
  'This submission contains participants in a terminating defined benefit plan. We are unable to establish accounts for participants of terminating defined benefit plans, unless the plan is not covered by the Pension Benefit Guaranty Corporation (PBGC).'
const ActiveBenefitTerminatingDefinedParagraphTwo_TAXABLE =
  'Most private sector defined benefit plans are covered by the PBGC, but there are some exceptions. <strong>It is the Plan Fiduciary\u2019s responsibility to determine whether the plan is covered by the PBGC.</strong>'
const TerminatingDefinedBenefitModal = (props) => (
  <IneligiblePlanModal {...props}>
    <div className="modal-header">
      <h1>Terminating defined benefit plan(s)</h1>
    </div>
  </IneligiblePlanModal>
)

const ActiveBenefitAboveThresholdModal = (props) => (
  <IneligiblePlanModal {...props}>
    <div className="modal-header">
      <h1>
        Active plan(s) &#8211; participants with balances in excess of $7,000
      </h1>
    </div>
  </IneligiblePlanModal>
)

const propTypes = {
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  setPlanConfirmationAcknowledgementsForm: PropTypes.func.isRequired,
  planConfirmationAcknowledgementsForm: PropTypes.object,
  fetchCompletedServicesAgreements: PropTypes.func.isRequired,
  updatePlanAgreement: PropTypes.func.isRequired,
  participantsAgreementHash: PropTypes.object,
  completedServicesAgreements: PropTypes.arrayOf(
    Types.servicesAgreementSummary
  ),
  setDiscardedParticipants: PropTypes.func.isRequired,
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  participantFileName: PropTypes.string,
  agreementsMapping: PropTypes.string.isRequired,
  nextPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  uploadFilePath: PropTypes.string.isRequired,
  allParticipantsRemovedPath: PropTypes.string.isRequired,
  recordType: PropTypes.string.isRequired,
  PlanConfirmationFormComponent: PropTypes.elementType.isRequired,
  getActiveBenefitAboveThresholdParticipants: PropTypes.func,
  getTerminatingDefinedBenefitParticipants: PropTypes.func,
  currentUser: Types.user,
}

const defaultProps = {
  planConfirmationAcknowledgementsForm: {},
  participantsAgreementHash: null,
  completedServicesAgreements: null,
  participantFileName: null,
  getActiveBenefitAboveThresholdParticipants: null,
  getTerminatingDefinedBenefitParticipants: null,
  currentUser: null,
}

function PlanConfirmationView({
  participantsForSubmission,
  flashErrorMessageHandler,
  setPlanConfirmationAcknowledgementsForm,
  planConfirmationAcknowledgementsForm,
  fetchCompletedServicesAgreements,
  updatePlanAgreement,
  participantsAgreementHash,
  completedServicesAgreements,
  setDiscardedParticipants,
  discardedParticipants,
  participantFileName,
  agreementsMapping,
  nextPath,
  uploadFilePath,
  allParticipantsRemovedPath,
  recordType,
  PlanConfirmationFormComponent,
  getActiveBenefitAboveThresholdParticipants,
  getTerminatingDefinedBenefitParticipants,
  currentUser,
}) {
  const history = useHistory()
  const formRef = useRef()
  // grandfathered clients are exempt from the involuntary distribution eligibility checks
  const isGrandfatheredUser =
    currentUser?.grandfatheredUser === GrandfatheredUserValue
  const hasActiveBenefitCheck =
    !!getActiveBenefitAboveThresholdParticipants && !isGrandfatheredUser
  const hasTerminatingDefinedBenefitCheck =
    !!getTerminatingDefinedBenefitParticipants && !isGrandfatheredUser

  const [
    showActiveBenefitAboveThresholdModal,
    setShowActiveBenefitAboveThresholdModal,
  ] = useState(false)
  const [
    showTerminatingDefinedBenefitPlanModal,
    setShowTerminatingDefinedBenefitPlanModal,
  ] = useState(false)
  const [showAllParticipantsRemovedModal, setShowAllParticipantsRemovedModal] =
    useState(false)
  const activeBenefitAboveThresholdParticipants = useRef(null)
  const terminatedDefinedBenefitParticipants = useRef(null)
  const activeBenefitsType = useRef(ActiveBenefitsPlanType.NONE)
  // workaround for race condition between data in the redux store and form submission:
  const participantsToSubmit = useRef()
  participantsToSubmit.current = participantsForSubmission
  const participantsToDiscard = useRef()
  participantsToDiscard.current = discardedParticipants

  const getParticipantsWithAgreementData = (participants = []) =>
    participants.map(
      (participant) =>
        participantsToSubmit.current?.find(
          ({ row }) => row === participant.row
        ) || participant
    )
  const plansInitialValues = useMemo(
    () =>
      reduce(
        participantsAgreementHash,
        (acc, { servicesAgreement: id, participants }) => {
          if (id === NO_SERVICES_AGREEMENT_OPTION.value) return acc
          const serviceAgreement = completedServicesAgreements?.find(
            ({ serviceAgreementID }) => serviceAgreementID === id
          )
          if (!serviceAgreement) return acc
          // It is possible for the same services agreement to be stored under multiple keys in participantsAgreementHash
          // (if there were more than one version of the plan name value in the uploaded file, e.g., "Cyclades DB" vs "Cyclades DB Plan")
          if (acc[id]) {
            acc[id].participants = concat(acc[id].participants, participants)
            return acc
          }
          acc[id] = {
            planType: getSelectInitialValue(
              serviceAgreement.planType,
              planTypes
            ),
            planStatus: getSelectInitialValue(
              serviceAgreement.planStatus,
              planStatuses
            ),
            planName: serviceAgreement.name,
            participants,
          }
          return acc
        },
        {}
      ),
    [participantsAgreementHash, completedServicesAgreements]
  )

  const submitData = async (values) => {
    formRef.current.setSubmitting(true)
    try {
      let promises = []
      forIn(values, ({ planStatus, planType }, id) => {
        if (id !== 'confirm') {
          promises.push(updatePlanAgreement(id, { planStatus, planType }))
        }
      })
      await Promise.allSettled(promises)
      await fetchCompletedServicesAgreements(agreementsMapping)
      setPlanConfirmationAcknowledgementsForm({ confirm: values.confirm })
      if (isEmpty(participantsToSubmit.current)) {
        formRef.current.setSubmitting(false)
        return setShowAllParticipantsRemovedModal(true)
      }
      const destination =
        typeof nextPath === 'function'
          ? nextPath(values, history)
          : {
              pathname: nextPath,
              state: { search: history.location.state?.search },
            }
      history.push(destination)
    } catch (e) {
      formRef.current.setSubmitting(false)
      flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    }
  }

  const handleActiveBenefitCheck = (valuesToSubmit, setSubmitting) => {
    activeBenefitAboveThresholdParticipants.current =
      getActiveBenefitAboveThresholdParticipants(
        valuesToSubmit,
        activeBenefitsType
      )
    if (activeBenefitAboveThresholdParticipants.current.length) {
      if (hasTerminatingDefinedBenefitCheck) {
        terminatedDefinedBenefitParticipants.current =
          getTerminatingDefinedBenefitParticipants(
            valuesToSubmit,
            activeBenefitsType
          )
      }
      setSubmitting(false)
      setShowActiveBenefitAboveThresholdModal(true)
      return true
    }
    setDiscardedParticipants({
      participants: [],
      reasonForDiscard:
        ReasonForDiscard.ACTIVE_BENEFIT_ABOVE_ALLOWABLE_THRESHOLD,
    })
    return false
  }

  const handleTerminatingBenefitCheck = (valuesToSubmit, setSubmitting) => {
    terminatedDefinedBenefitParticipants.current =
      getTerminatingDefinedBenefitParticipants(
        valuesToSubmit,
        activeBenefitsType
      )
    if (terminatedDefinedBenefitParticipants.current.length) {
      setSubmitting(false)
      setShowTerminatingDefinedBenefitPlanModal(true)
      return true
    }
    const terminatingDiscardedParticipants = discardedParticipants.filter(
      (participant) =>
        participant.reasonForDiscard ===
        ReasonForDiscard.TERMINATING_DEFINED_BENEFIT
    )

    setDiscardedParticipants({
      participants: [],
      reasonForDiscard: ReasonForDiscard.TERMINATING_DEFINED_BENEFIT,
    })

    // Keep submit path in sync for this click, before redux re-render catches up
    if (
      isEmpty(participantsToSubmit.current) &&
      terminatingDiscardedParticipants.length
    ) {
      participantsToSubmit.current = concat(
        participantsToSubmit.current,
        terminatingDiscardedParticipants
      )
    }
    return false
  }

  const handleSubmit = (values, { setSubmitting }) => {
    if (!hasActiveBenefitCheck && !hasTerminatingDefinedBenefitCheck) {
      return submitData(values)
    }

    const valuesToSubmit = omit(values, 'confirm')
    activeBenefitsType.current = ActiveBenefitsPlanType.NONE

    if (
      hasActiveBenefitCheck &&
      handleActiveBenefitCheck(valuesToSubmit, setSubmitting)
    ) {
      return
    }

    if (
      hasTerminatingDefinedBenefitCheck &&
      handleTerminatingBenefitCheck(valuesToSubmit, setSubmitting)
    ) {
      return
    }

    return submitData(values)
  }

  if (isEmpty(participantsAgreementHash)) {
    return <Redirect to={uploadFilePath} />
  }

  return (
    <div className="card full-height upload-participant-file-view-container">
      <div className="form-section">
        <PlanConfirmationFormComponent
          initialValues={{
            ...plansInitialValues,
            confirm: false,
            ...planConfirmationAcknowledgementsForm,
          }}
          onSubmit={handleSubmit}
          ref={formRef}
        />
      </div>
      {hasActiveBenefitCheck && showActiveBenefitAboveThresholdModal && (
        <ActiveBenefitAboveThresholdModal
          planNames={activeBenefitAboveThresholdParticipants.current?.map(
            ({ planName }) => planName
          )}
          activeBenefitsType={activeBenefitsType.current}
          planListTitle="Active plans - participants with balances exceeding $7,000"
          disclosureParagraphOne={ActiveBenefitExceedingThresholdParagraphOne}
          disclosureParagraphTwo={ActiveBenefitExceedingThresholdParagraphTwo}
          checkBoxUndertaking={PlansExceedingUndertakings.ACTIVE}
          onClose={() => setShowActiveBenefitAboveThresholdModal(false)}
          onContinue={({ submissionMethod, confirmLegal }) => {
            if (submissionMethod === 'proceedWithSubmission') {
              if (confirmLegal) {
                setShowActiveBenefitAboveThresholdModal(false)
                if (terminatedDefinedBenefitParticipants.current?.length) {
                  return setShowTerminatingDefinedBenefitPlanModal(true)
                } else {
                  setDiscardedParticipants({
                    participants: [],
                    reasonForDiscard:
                      ReasonForDiscard.TERMINATING_DEFINED_BENEFIT,
                  })
                }
                submitData(formRef.current.values)
              }
            } else {
              const discardParticipants =
                submissionMethod === removeFromSubmissionOption.value
              setShowActiveBenefitAboveThresholdModal(false)
              setDiscardedParticipants({
                participants: discardParticipants
                  ? getParticipantsWithAgreementData(
                      activeBenefitAboveThresholdParticipants.current
                    )
                  : [],
                reasonForDiscard:
                  ReasonForDiscard.ACTIVE_BENEFIT_ABOVE_ALLOWABLE_THRESHOLD,
              })
              if (terminatedDefinedBenefitParticipants.current?.length) {
                return setShowTerminatingDefinedBenefitPlanModal(true)
              } else {
                setDiscardedParticipants({
                  participants: [],
                  reasonForDiscard:
                    ReasonForDiscard.TERMINATING_DEFINED_BENEFIT,
                })
              }
              submitData(formRef.current.values)
            }
          }}
        />
      )}
      {hasTerminatingDefinedBenefitCheck &&
        showTerminatingDefinedBenefitPlanModal && (
          <TerminatingDefinedBenefitModal
            planNames={terminatedDefinedBenefitParticipants.current?.map(
              ({ planName }) => planName
            )}
            activeBenefitsType={activeBenefitsType.current}
            planListTitle="Terminating defined benefit plans"
            disclosureParagraphOne={
              recordType === RecordTypes.TAXABLE_ACCOUNTS_PARTICIPANT
                ? ActiveBenefitTerminatingDefinedParagraphOne_TAXABLE
                : ActiveBenefitTerminatingDefinedParagraphOne
            }
            disclosureParagraphTwo={
              recordType === RecordTypes.TAXABLE_ACCOUNTS_PARTICIPANT
                ? ActiveBenefitTerminatingDefinedParagraphTwo_TAXABLE
                : ActiveBenefitTerminatingDefinedParagraphTwo
            }
            checkBoxUndertaking={
              recordType === RecordTypes.TAXABLE_ACCOUNTS_PARTICIPANT
                ? PlansExceedingUndertakings.TERMINATING_TAXABLE
                : PlansExceedingUndertakings.TERMINATING
            }
            onClose={() => setShowTerminatingDefinedBenefitPlanModal(false)}
            onContinue={({ submissionMethod }) => {
              const discardParticipants =
                submissionMethod === removeFromSubmissionOption.value
              setDiscardedParticipants({
                participants: discardParticipants
                  ? getParticipantsWithAgreementData(
                      terminatedDefinedBenefitParticipants.current
                    )
                  : [],
                reasonForDiscard: ReasonForDiscard.TERMINATING_DEFINED_BENEFIT,
              })
              setShowTerminatingDefinedBenefitPlanModal(false)
              submitData(formRef.current.values)
            }}
          />
        )}
      {showAllParticipantsRemovedModal && (
        <AllParticipantsRemovedModal
          onClose={() => setShowAllParticipantsRemovedModal(false)}
          onConfirm={() => {
            setShowAllParticipantsRemovedModal(false)
            history.push({
              pathname: allParticipantsRemovedPath,
              state: {
                ignorePrompt: true,
                state: { search: history.location.state?.search },
              },
            })
          }}
          handleDownload={() =>
            downloadDiscardedParticipants({
              participants: participantsToDiscard.current,
              originalFileName: participantFileName,
              recordType,
            })
          }
          recordType={recordType}
        />
      )}
    </div>
  )
}

PlanConfirmationView.propTypes = propTypes
PlanConfirmationView.defaultProps = defaultProps

export default PlanConfirmationView
